import React, { useState } from "react";
import "./css/DivisionShowcase.css";

const DIVISIONS = {
  "app-development": {
    name: "App Development",
    tagline: "Custom applications built for the modern world",
    description: "Our App Development division creates innovative, user-focused applications that push the boundaries of what's possible. From concept to deployment, we build scalable solutions for web, mobile, and emerging platforms. Our team specialises in React, Node.js, and modern cloud technologies to deliver apps that perform and delight users.",
    services: [
      {
        title: "Custom Web Applications",
        detail: "Full-stack web apps built with modern frameworks like React and Node.js, optimised for performance, SEO, and seamless user experiences across all devices.",
      },
      {
        title: "Mobile App Development",
        detail: "Native and cross-platform mobile apps for iOS and Android that feel smooth and fast, from design and prototyping through to App Store and Play Store launch.",
      },
      {
        title: "API Development & Integration",
        detail: "Robust RESTful and GraphQL APIs that connect your systems, plus integrations with third-party platforms, payment providers, and data sources.",
      },
      {
        title: "Cloud Infrastructure",
        detail: "Scalable cloud architecture on AWS, GCP, or Azure including CI/CD pipelines, serverless functions, containerised deployments, and 24/7 monitoring.",
      },
      {
        title: "UI/UX Design",
        detail: "User research, wireframing, and pixel-perfect interface design that balances aesthetics with intuitive usability, grounded in real user behaviour.",
      },
      {
        title: "Technical Consulting",
        detail: "Strategy sessions, architecture reviews, and code audits to help your team move faster and make better technical decisions with confidence.",
      },
    ],
    accent: "#4169e1",
    glow: "65, 105, 225",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <rect x="2" y="3" width="20" height="14" rx="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
        <polyline points="9 9 12 6 15 9" strokeLinejoin="round"/>
        <line x1="12" y1="6" x2="12" y2="14"/>
      </svg>
    ),
  },
  "music-entertainment": {
    name: "Music & Entertainment",
    tagline: "Record label, music production, and digital distribution",
    description: "Our Music & Entertainment division is dedicated to discovering, developing, and distributing exceptional talent. We provide full-service music production, artist management, and digital distribution to get your music heard worldwide. From recording studios to streaming platforms, we handle every aspect of your music career.",
    services: [
      {
        title: "Artist Development",
        detail: "Coaching, branding, and growth strategy tailored to each artist, from image building and social presence to audience development and long-term career planning.",
      },
      {
        title: "Music Production",
        detail: "Professional recording, mixing, and mastering services across all genres, with access to our in-house studio and an extensive network of producers and engineers.",
      },
      {
        title: "Digital Distribution",
        detail: "Get your music on Spotify, Apple Music, Tidal, and 150+ platforms worldwide within days, with full royalty reporting and rights management.",
      },
      {
        title: "Playlist Pitching",
        detail: "Direct editorial pitching to curators on major streaming platforms to boost organic streams, listener discovery, and placement in high-traffic playlists.",
      },
      {
        title: "Music Licensing",
        detail: "Placement of your music in TV shows, films, ads, and digital content, generating sync royalties and broad exposure to new audiences.",
      },
      {
        title: "Live Event Management",
        detail: "End-to-end coordination of live performances, headline tours, and pop-up events, from booking and logistics to day-of production and audience experience.",
      },
    ],
    accent: "#ff1493",
    glow: "255, 20, 147",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M9 18V5l12-2v13"/>
        <circle cx="6" cy="18" r="3"/>
        <circle cx="18" cy="16" r="3"/>
      </svg>
    ),
  },
  "branding": {
    name: "Branding",
    tagline: "Brand building, marketing, and advertising solutions",
    description: "Our Branding division transforms brands through strategic marketing and creative excellence. We offer comprehensive branding, advertising, and content creation services that help businesses stand out in crowded markets. Our data-driven approach ensures every campaign delivers measurable results.",
    services: [
      {
        title: "Brand Strategy & Identity",
        detail: "Deep-dive discovery sessions leading to a complete brand system: logo, typography, colour palette, tone of voice, and brand guidelines your whole team can use.",
      },
      {
        title: "Digital Advertising",
        detail: "Performance-driven paid campaigns across Meta, Google, TikTok, and YouTube, built around clear objectives and continuously optimised for return on investment.",
      },
      {
        title: "Social Media Marketing",
        detail: "Content calendars, community management, and audience growth strategies that build real, engaged followings across every major social platform.",
      },
      {
        title: "Content Creation",
        detail: "Photo, video, and copywriting production that tells your brand story in a compelling, shareable way, designed for digital-first consumption.",
      },
      {
        title: "Video Production",
        detail: "Concept to delivery: scripting, filming, editing, colour grading, and motion graphics for ads, brand films, testimonials, and social content.",
      },
      {
        title: "Influencer Partnerships",
        detail: "Identifying, vetting, and managing partnerships with creators who genuinely align with your brand values, audience, and campaign objectives.",
      },
    ],
    accent: "#8a2be2",
    glow: "138, 43, 226",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
        <path d="M2 17l10 5 10-5"/>
        <path d="M2 12l10 5 10-5"/>
      </svg>
    ),
  },
  "news-media": {
    name: "News & Media Agency",
    tagline: "Digital news and media coverage for the connected world",
    description: "Our News & Media Agency delivers timely, accurate, and engaging coverage of the stories that matter. We specialise in digital journalism, multimedia storytelling, and real-time reporting across technology, entertainment, and culture. Our platform reaches audiences worldwide with content that informs and inspires.",
    services: [
      {
        title: "Digital Journalism",
        detail: "In-depth reporting and news articles published across our digital channels, crafted by experienced journalists and reaching a global readership daily.",
      },
      {
        title: "Breaking News Coverage",
        detail: "Around-the-clock monitoring and rapid-response reporting on developing stories across tech, entertainment, business, and culture as they happen.",
      },
      {
        title: "Feature Stories",
        detail: "Long-form editorial pieces, profiles, and investigations that go beyond the headline to explore what really matters and why it affects your world.",
      },
      {
        title: "Podcast Production",
        detail: "Full-service podcast creation including concept development, recording, editing, branding, cover art, and distribution to all major podcast platforms.",
      },
      {
        title: "Video Documentaries",
        detail: "Short and long-form documentary production exploring the people, stories, and ideas shaping culture, from research and scripting to final delivery.",
      },
      {
        title: "Press Release Distribution",
        detail: "Wide-network distribution of your press releases and announcements to journalists, bloggers, industry contacts, and relevant media outlets.",
      },
    ],
    accent: "#ff69b4",
    glow: "255, 105, 180",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
        <path d="M18 14h-8"/>
        <path d="M15 18h-5"/>
        <path d="M10 6h8v4h-8V6Z"/>
      </svg>
    ),
  },
};

const DivisionShowcase = ({ divisionId, onNavigate }) => {
  const div = DIVISIONS[divisionId];
  const [openIdx, setOpenIdx] = useState(null);

  if (!div) {
    return (
      <div className="ds-page">
        <div className="ds-error">
          <p>Division not found.</p>
          <button onClick={() => onNavigate("divisions")}>Back to Divisions</button>
        </div>
      </div>
    );
  }

  const style = { "--accent": div.accent, "--glow": div.glow };

  const toggle = (i) => setOpenIdx(prev => prev === i ? null : i);

  return (
    <div className="ds-page" style={style}>

      <section className="ds-hero">
        <button className="ds-back" onClick={() => onNavigate("divisions")}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          All Divisions
        </button>

        <div className="ds-hero-inner">
          <div className="ds-icon">{div.icon}</div>
          <h1>{div.name}</h1>
          <p className="ds-tagline">{div.tagline}</p>
        </div>
      </section>

      <div className="ds-body">

        <div className="ds-grid">

          <section className="ds-about">
            <p className="ds-section-label">About</p>
            <p className="ds-desc">{div.description}</p>
          </section>

          <section className="ds-services">
            <p className="ds-section-label">What We Offer</p>
            <ul className="ds-services-list">
              {div.services.map((s, i) => (
                <li key={i}>
                  <button
                    className={`ds-service-item${openIdx === i ? " open" : ""}`}
                    onClick={() => toggle(i)}
                    aria-expanded={openIdx === i}
                  >
                    <span className="ds-service-num">0{i + 1}</span>
                    <span className="ds-service-title">{s.title}</span>
                    <span className="ds-service-chevron">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </span>
                  </button>
                  {openIdx === i && (
                    <p className="ds-service-detail">{s.detail}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>

        </div>

        <div className="ds-cta">
          <div className="ds-cta-inner">
            <h2>Ready to Work Together?</h2>
            <p>Get in touch and let's bring your vision to life.</p>
            <div className="ds-cta-buttons">
              <button className="ds-cta-primary" onClick={() => onNavigate("contact")}>
                Contact Us
              </button>
              <button className="ds-cta-ghost" onClick={() => onNavigate("divisions")}>
                Explore Other Divisions
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DivisionShowcase;
